import {priority} from '@priority-software/priority-proto-api/lib';
import {CheckBox, Flex, Link, Text} from '@priority-software/priority-style-react';
import React, {memo, useEffect, useState, useCallback, useRef} from 'react';
import {useQuery} from 'react-query';
import cx from 'classnames';
import {SubformItem} from './SubformItem';
import {highlightText} from '../../../../_legacy/library/utils/highlightText';
import {getFormColums} from '../../services/service';
import styles from './styles.module.scss';
import {serializeFields} from './utils';
import {createDataTestId} from '../../../../utils/dataTestId';
import {updateOuterJoinMap} from '../../actions';
import {BasicFormData} from '../../types';
import texts from '../../texts';
import {CALCULATED_FORMS_ENAMES} from '../../consts';
import {ModernTooltip} from '../../../../components/Common/Tooltip/ModernTooltip';
import {reportGeneratorsStore} from '../../stores/ReportGeneratorsStore';

interface Props {
    field: priority.netitems.nrest.ITreeNode;
    onSelect: (field: priority.netitems.nrest.ITreeNode, currentForm: BasicFormData) => void;
    isSelected: (id: string) => boolean;
    onZoom: (Nodeid: string, title: string, currentForm: BasicFormData) => void;
    isSubformField?: boolean;
    searchQuery: string;
    currentForm: BasicFormData;
}

const FieldItem = memo(
    ({field, onSelect, isSelected, onZoom, isSubformField, searchQuery, currentForm}: Props) => {
        const [isOpen, setIsOpen] = useState(false);
        const [isMatchQuery, setIsMatchQuery] = useState(true);
        const isSubFormItem =
            field.Type === priority.netitems.nrest.TreeNodeTypes.TreeNodeTypes_Form;
        const isDisabled = CALCULATED_FORMS_ENAMES.includes(field.FormName);

        // Reference to this field item element for scrolling
        const fieldItemRef = useRef<HTMLDivElement>(null);

        const {data: fields} = useQuery(
            ['subform', field.NodeId],
            () => getFormColums(field.NodeId),
            {
                enabled: isOpen && isSubFormItem,
                staleTime: Infinity,
                select: data => serializeFields(data)
            }
        );

        useEffect(() => {
            let isMatch = false;
            if (!!searchQuery) {
                if (
                    field.Title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    field?.FormTitle?.toLowerCase().includes(searchQuery.toLowerCase())
                ) {
                    isMatch = true;
                }
                if (isSubFormItem && fields) {
                    const hasMatchFields = fields.some(fld =>
                        fld.Title.toLowerCase().includes(searchQuery.toLowerCase())
                    );
                    if (hasMatchFields) {
                        isMatch = true;
                        !isOpen && setIsOpen(true);
                    }
                }
            } else {
                isMatch = true;
            }

            setIsMatchQuery(isMatch);
        }, [searchQuery]);

        // Handle toggling the subform open/closed
        const toggleOpen = useCallback(() => {
            if (isDisabled) {
                return;
            }
            const node = {
                id: field.NodeId,
                title: field.Title,
                nodes: []
            };
            updateOuterJoinMap(node, currentForm);

            // Toggle the open state
            setIsOpen(prev => !prev);
        }, [isDisabled, field.NodeId, field.Title, currentForm]);

        // Handle arrow click with search clearing
        const onSubFormItemClick = useCallback(() => {
            // If we're opening the subform and there's a search query, clear it
            if (!isOpen && searchQuery) {
                const {currentScreen} = reportGeneratorsStore;
                if (currentScreen && currentScreen.currentReport) {
                    // Store the current element before clearing search
                    const currentElement = fieldItemRef.current;

                    // Clear the search query
                    currentScreen.currentReport.setSearchQuery('');

                    // After clearing search, scroll to the expanded subform
                    // Use requestAnimationFrame to ensure the DOM has updated
                    requestAnimationFrame(() => {
                        if (currentElement) {
                            // Use a small timeout to avoid the jump
                            setTimeout(() => {
                                // Scroll the element into view with auto behavior to avoid jumps
                                currentElement.scrollIntoView({
                                    behavior: 'auto',
                                    block: 'start'
                                });
                            }, 50);
                        }
                    });
                }
            }

            // Then toggle the subform
            toggleOpen();
        }, [isOpen, searchQuery, toggleOpen]);

        const fieldClasses = cx(styles.selectFieldItem, {
            [styles.fieldItemBorder]: !isSubformField,
            [styles.disabledField]: isDisabled
        });

        if (!isMatchQuery) {
            return null;
        }

        if (!isSubFormItem) {
            return (
                <div
                    className={fieldClasses}
                    {...createDataTestId('new-report-select-fields-list-item')}
                >
                    <CheckBox
                        onChange={() => onSelect(field, currentForm)}
                        checked={isSelected(field.NodeId)}
                    >
                        <Flex justifyContent="flex-start" alignItems="center">
                            <Text
                                textColor="primary-700"
                                skin="paragraph-2"
                                {...createDataTestId(
                                    'new-report-select-fields-list-item-highlight',
                                    true
                                )}
                            >
                                {highlightText(field.Title, searchQuery, true, false, true)}
                            </Text>
                            {!!field.FormTitle && (
                                <Link
                                    componentClasses={styles.linkItem}
                                    onClick={e => {
                                        e.stopPropagation();
                                        onZoom(field.ZoomFormNodeId, field.FormTitle, currentForm);
                                    }}
                                >
                                    (
                                    {highlightText(field.FormTitle, searchQuery, true, false, true)}
                                    )
                                </Link>
                            )}
                        </Flex>
                    </CheckBox>
                </div>
            );
        }

        return (
            <Flex
                ref={fieldItemRef}
                componentClasses={fieldClasses}
                justifyContent="flex-start"
                alignItems="flex-start"
                direction="column"
                {...createDataTestId(`new-report-select-fields-subForm-list-${field.Title}`, true)}
            >
                <div onClick={onSubFormItemClick}>
                    {isDisabled ? (
                        <ModernTooltip tooltip={texts.calcFormsTooltip} position="bottom-start">
                            <SubformItem
                                isOpen={isOpen}
                                isDisabled={isDisabled}
                                title={field.Title}
                                searchQuery={searchQuery}
                            />
                        </ModernTooltip>
                    ) : (
                        <SubformItem
                            isOpen={isOpen}
                            isDisabled={isDisabled}
                            title={field.Title}
                            searchQuery={searchQuery}
                        />
                    )}
                </div>
                {isOpen && (
                    <Flex
                        alignItems="flex-start"
                        justifyContent="flex-start"
                        direction="column"
                        componentClasses={styles.subFormList}
                    >
                        {fields?.map(fld => (
                            <FieldItem
                                key={fld.NodeId}
                                isSelected={isSelected}
                                field={fld}
                                isSubformField={true}
                                onZoom={onZoom}
                                currentForm={{id: field.NodeId, title: field.Title}}
                                searchQuery={searchQuery}
                                onSelect={onSelect}
                            />
                        ))}
                    </Flex>
                )}
            </Flex>
        );
    }
);

export default FieldItem;
